import { Box, Grid, Stack } from '@mui/material';
import { useMemo } from 'react';
import {
  AnalyticsQueryTypeEnum,
  CelenderTypeEmun,
} from '../../../../features/analyticsDashboardSlice';
import {
  AnalyticsQueryParamsProps,
  AnalyticsQueryRequest,
} from '../../../../types/request/analyticsRequest';
import TeleCallersGraph from '../../components/TeleCallersGraph';
import CollectionTeamGraph from '../../components/CollectionTeamGraph';
import YTDPenalCollection from './modules/YTDPenalCollection';
import YTDAccountMovementGraph from './modules/YTDAccountMovementGraph';
import { BORDER_COLLECTION, COMMON } from '../../../../constants/style';
import VegaText from '../../../../components/common/VegaText';
import { ROUTES } from '../../../../router/routes';
import { GraphTypeEnum } from '../../AnalyticsDashboard';
import { useNavigate } from 'react-router-dom';
import RoleBaseSegmentPerformance from '../../components/RoleBaseSegmentPerformance';

interface IProps {
  selectedCollectionProfitType: string;
}

const YearToDateContainer = ({ selectedCollectionProfitType }: IProps) => {
  const navigate = useNavigate();
  const request = {
    analyticsQueryType: AnalyticsQueryTypeEnum.BUCKET_PERFORMANCE,
    params: {
      isMonthToDate: 'false',
      isYearToDate: 'true',
      type: selectedCollectionProfitType,
    },
  } as AnalyticsQueryRequest;

  const teleCallersGraph = useMemo(() => {
    const updatedParams = {
      ...request.params,
      role: AnalyticsQueryTypeEnum.TELE_COLLECTION_EXECUTIVE,
    } as AnalyticsQueryParamsProps;
    const requestUpdate = {
      ...request,
      params: updatedParams,
    } as AnalyticsQueryRequest;
    return (
      <Box
        sx={{
          transition: 'all 0.3s ease-in-out',
          cursor: 'pointer',
          ':hover': {
            transform: 'scale(1.01)',
          },
        }}
        onDoubleClick={() =>
          navigate(
            ROUTES.ANALYTICS_PARAMS.replace(
              ':viewType',
              GraphTypeEnum.TELE_CALLERS
            )
              .replace(':calenderType', CelenderTypeEmun.YEAR)
              .replace(':profitType', selectedCollectionProfitType)
          )
        }
      >
        <TeleCallersGraph
          request={requestUpdate}
          calenderType={CelenderTypeEmun.YEAR}
          selectedCollectionProfitType={selectedCollectionProfitType}
        />
      </Box>
    );
  }, [selectedCollectionProfitType]);

  const collectionTeamGraph = useMemo(() => {
    const updatedParams = {
      ...request.params,
      role: AnalyticsQueryTypeEnum.CE,
    } as AnalyticsQueryParamsProps;
    const requestUpdate = {
      ...request,
      params: updatedParams,
    } as AnalyticsQueryRequest;
    return (
      <Box
        sx={{
          transition: 'all 0.3s ease-in-out',
          cursor: 'pointer',
          ':hover': {
            transform: 'scale(1.01)',
          },
        }}
        onDoubleClick={() =>
          navigate(
            ROUTES.ANALYTICS_PARAMS.replace(
              ':viewType',
              GraphTypeEnum.COLLECTION_TEAM
            )
              .replace(':calenderType', CelenderTypeEmun.YEAR)
              .replace(':profitType', selectedCollectionProfitType)
          )
        }
      >
        <CollectionTeamGraph
          request={requestUpdate}
          calenderType={CelenderTypeEmun.YEAR}
          selectedCollectionProfitType={selectedCollectionProfitType}
          headerText="Collection Team (in %)"
        />
      </Box>
    );
  }, [selectedCollectionProfitType]);

  const penalChargeCollectionGraphForYear = useMemo(() => {
    return (
      <Box
        sx={{
          transition: 'all 0.3s ease-in-out',
          cursor: 'pointer',
          ':hover': {
            transform: 'scale(1.01)',
          },
        }}
        onDoubleClick={() =>
          navigate(
            ROUTES.ANALYTICS_PARAMS.replace(
              ':viewType',
              GraphTypeEnum.PENAL_COLLECTION
            )
              .replace(':calenderType', CelenderTypeEmun.YEAR)
              .replace(':profitType', selectedCollectionProfitType)
          )
        }
      >
        <YTDPenalCollection
          selectedCollectionProfitType={selectedCollectionProfitType}
        />
      </Box>
    );
  }, [selectedCollectionProfitType]);

  const accountMovementGraphForYear = useMemo(() => {
    return (
      <Box
        sx={{
          transition: 'all 0.3s ease-in-out',
          cursor: 'pointer',
          ':hover': {
            transform: 'scale(1.01)',
          },
        }}
        onDoubleClick={() =>
          navigate(
            ROUTES.ANALYTICS_PARAMS.replace(
              ':viewType',
              GraphTypeEnum.ACCOUNT_MOVEMENT
            )
              .replace(':calenderType', CelenderTypeEmun.YEAR)
              .replace(':profitType', selectedCollectionProfitType)
          )
        }
      >
        <YTDAccountMovementGraph
          selectedCollectionProfitType={selectedCollectionProfitType}
        />
      </Box>
    );
  }, [selectedCollectionProfitType]);

  const roleBaseSegmentForYear = useMemo(() => {
    return (
      <Box
        sx={{
          transition: 'all 0.3s ease-in-out',
          cursor: 'pointer',
        }}
        onDoubleClick={() =>
          navigate(
            ROUTES.ANALYTICS_PARAMS.replace(
              ':viewType',
              GraphTypeEnum.ROLE_BASE
            )
              .replace(':calenderType', CelenderTypeEmun.YEAR)
              .replace(':profitType', selectedCollectionProfitType)
          )
        }
      >
        <RoleBaseSegmentPerformance
          selectedCollectionProfitType={selectedCollectionProfitType}
          calenderType={CelenderTypeEmun.YEAR}
        />
      </Box>
    );
  }, [selectedCollectionProfitType]);

  return (
    <>
      <Stack
        sx={{
          p: 3,
          bgcolor: 'white',
          borderRadius: '12px',
          border: BORDER_COLLECTION,
        }}
        gap={2}
      >
        <VegaText
          text={'Year to Date'}
          fontSize={17}
          fontWeight={600}
          color={COMMON.balck}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6}>
            {collectionTeamGraph}
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            {teleCallersGraph}
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            {accountMovementGraphForYear}
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            {penalChargeCollectionGraphForYear}
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            {roleBaseSegmentForYear}
          </Grid>
        </Grid>
      </Stack>
    </>
  );
};

export default YearToDateContainer;
